import '../../../../Leaflet.glify/glify.js'; // FIXME need to include as external in Parcel 2
import Geography from '../Geography';
import AbstractLayerManager from './AbstractLayerManager.js';
// import { pal_radar_refl_default } from '../pal/radar_refl_default.js';
import Palette from '../../pal/radar_refl_default.js';
import Request from '../../Request';
import WECDM from '../WECDM';

// v4.0
//
// REFS:
// https://github.com/robertleeplummerjr/Leaflet.glify
// https://limnu.com/webgl-blending-youre-probably-wrong/
//
// v1-v3 refs:
// https://www.html5rocks.com/en/tutorials/webgl/typed_arrays/
// http://bl.ocks.org/Sumbera/c6fed35c377a46ff74c3
// https://github.com/Sumbera/gLayers.Leaflet
// https://blog.sumbera.com/2014/07/28/webgl-polygons-fill-with-libtess-js/
// http://bl.ocks.org/xEviL/4921fff1d70f5601d159
// https://github.com/Leaflet/Leaflet/issues/3738
//
// http://almanydesigns.com/grx/reflectivity/
// https://mesonet.agron.iastate.edu/GIS/rasters.php?rid=2
//

export default class GLRadarLayer extends AbstractLayerManager {
	constructor(myKey, instance) {
		super(myKey, instance);

		this.name = "HD Site Radar";
		this.popupName = "Radar Detail";
		this.options.hasPanRefresh = false;
		this.options.hasAutoRefresh = true;

		this.center = null;
		this.rayDist = 0;
		this.beamOffset = 0;
		this.fudgeR = 0.0;
		this.fudgeG = 0.0;
		this.gateEndFudge = 0.0;
		this.gateData = [];
		
		this.color_pal_refl = Palette.pal_radar_refl_default;

		// Radar site selection
		this.layerSites = null;
		this.sites = [];
		this.siteFiles = [];

		// Current user settings
		this.site = null;
		this.var = null;
		this.tilt = null;
	}
	
	attachLayer() {
		if(this.isAttached()) return true;
		
		// Canvas layer
		this.inst.layers[this.id] = this.layer = window.L.glify.shapes({
			map: this.inst.map,
			data: this.gateData,
			opacity: 1.0
		});
		// this.inst.map.addLayer(this.layer);
		if(this.options.hasPanRefresh) this.inst.mapMoveListeners[this.id] = this.id;
		if(this.options.hasAutoRefresh) this.inst.autoUpdateListeners[this.id] = this.id;
		// this.refreshLayer();

		// Site selection layer
		this.layerSites = new L.LayerGroup();
		this.loadSiteSelection();
	}
	
	detachLayer() {
		if(!this.isAttached()) return true;

		this.inst.map.removeLayer(this.layerSites);
		this.layerSites = null;

		if(this.options.hasPanRefresh) delete(this.inst.mapMoveListeners[this.id]);
		if(this.options.hasAutoRefresh) delete(this.inst.autoUpdateListeners[this.id]);

		this.inst.map.removeLayer(this.layer.glLayer);
		delete(this.inst.layers[this.id]);
		this.layer = null;
	}

	loadData(file) {
		// File is WECDM class instance

		// TODO assert that WECDM instance is type=radial
		// TODO have WECDM.Radial subclass that can handle stream reading...
		
		var stream = file.stream;
		
		this.center = L.latLng([file.header.lat, file.header.lon]);
		this.rayDist = (file.header.ngates * file.header.gateSize) // m
		this.beamOffset = (360 / file.header.nrays / 2);
		this.fudgeR = 0.019;
		this.fudgeG = 0.0;
		// var gateEndFudge = (header.gateSize * (1+fudgeG));
		this.gateEndFudge = 0.0;
		
		// Clear gate data
		this.gateData = [];
		
		for(var r = 0; r < file.header.nrays; r++) {
			// Azimuth and data for this ray
			var azd = stream.readFloat32();
			var data = stream.readInt8Array(file.header.ngates);

			// Calculate beam edges for this azimuth
			var azBeamLeft  = azd - this.beamOffset - this.fudgeR;
			var azBeamRight = azd + this.beamOffset + this.fudgeR;

			// Initialize starting points for each ray as edge of cone of silence
			var prevEnd1 = Geography.destVincenty(this.center.lat, this.center.lng, azd - this.beamOffset, file.header.distFirstGate);
			var prevEnd2 = Geography.destVincenty(this.center.lat, this.center.lng, azd + this.beamOffset, file.header.distFirstGate);
			var needStarts = false;

			for(var rg = 0; rg < data.length; rg++) {
				// Get dBz value for this gate
				var refl = data[rg];
				if(refl === -127) {
					needStarts = true;
					continue;
				}

				var b = (refl / 2) + 31; // byte scale

				//b = (b/94.0) + 0.03;
				//var c = (255 - (refl + 127)); // TODO TEMP
				//var refColor = 'rgb(' + c + ',' + c + ',' + c + ')'
				// For NEXRAD data
				var palkey = "" + b.toFixed(1);
				if(!this.color_pal_refl[palkey]) {
				//        	console.log(b);
					needStarts = true;
					continue;
				}
				// var refColor = 'rgb(' + this.color_pal_refl[palkey].r + ',' + this.color_pal_refl[palkey].g + ',' + this.color_pal_refl[palkey].b + ')'

				var gateDistance = file.header.distFirstGate + (rg * file.header.gateSize);
				var gateDistanceEnd = file.header.distFirstGate + ((rg + 1) * file.header.gateSize);

				if(needStarts) {
					var start1 = Geography.destVincenty(this.center.lat, this.center.lng, azBeamLeft, gateDistance);
					var start2 = Geography.destVincenty(this.center.lat, this.center.lng, azBeamRight, gateDistance);
				} else {
					var start1 = prevEnd1;
					var start2 = prevEnd2;
				}

				var end1  = Geography.destVincenty(this.center.lat, this.center.lng, azBeamLeft, gateDistanceEnd);
				var end2  = Geography.destVincenty(this.center.lat, this.center.lng, azBeamRight, gateDistanceEnd);
				var end1F = Geography.destVincenty(this.center.lat, this.center.lng, azBeamLeft, gateDistanceEnd + this.gateEndFudge);
				var end2F = Geography.destVincenty(this.center.lat, this.center.lng, azBeamRight, gateDistanceEnd + this.gateEndFudge);
					
				prevEnd1 = end1;
				prevEnd2 = end2;

				//gateData.push([start1, start2, end2F, end1F, refColor]);
				this.gateData.push([start1, start2, end2F, end1F, this.color_pal_refl[palkey]]);
			}
		}
		
		console.log('completed calculating coords for NEXRAD gates');
		
	}

	refreshLayer() {
		// Trigger layer refresh
		
		if(this.sites) {
			// TODO kinda hacky
			var e = {
				target: {
					options: {
						_site: this.site
					}
				}
			};
			this.handleSiteSelected(e);
		}
	}

	showSiteSelector() {
		this.inst.map.addLayer(this.layerSites);
	}

	hideSiteSelector() {
		this.inst.map.removeLayer(this.layerSites);
	}

	loadSiteSelection() {
		this.inst.setLoadingMonitor('nexradl2_sites', true);
		if(this.tasks.fetchSites === undefined) this.tasks.fetchSites = new Request({
			url: 'https://api.weatherusa.net/v1/feed/radar',
			link: 'cancel',
			timeout: 30000,
			onSuccess: this.handleSiteListResponse.bind(this),
			onError: this.defaultErrorHandler.bind(this)
		});
		this.tasks.fetchSites.get();
	}

	handleSiteListResponse(resp) {
		this.sites = resp.body.data;

		for(let site of resp.body.data) {
			let siteLabel = new L.DivIcon({
				iconSize: [46, 22],
				className: 'radar-site-id',
				html: site.icao
			});
			new L.Marker(new L.LatLng(site.lat, site.lon), {
				icon: siteLabel,
				title: site.name + " " + site.state + " " + site.country,
				zIndexOffset: 2300,
				_site: site.icao
			})
			.on('click', this.handleSiteSelected, this)
			.addTo(this.layerSites);
		}

		this.showSiteSelector();

		this.inst.setLoadingMonitor('nexradl2_sites', false);
	}

	handleSiteSelected(e) {
		this.inst.setLoadingMonitor(this.id, true);
		if(this.tasks.fetchFileList === undefined) this.tasks.fetchFileList = new Request({
			url: 'https://api.weatherusa.net/v1/feed/gridfile',
			link: 'cancel',
			timeout: 30000,
			onSuccess: this.handleFileListResponse.bind(this),
			onError: this.defaultErrorHandler.bind(this)
		});
		this.site = e.target.options._site;
		this.tasks.fetchFileList.get({'site': e.target.options._site, 'mode': 'list'});
	}

	handleFileListResponse(resp) {
		this.siteFiles = resp.body;

		// TODO
		// Get latest 2ref for testing
		var fileName = this.siteFiles.latest['2ref'];

		new Request({
			url: 'https://api.weatherusa.net/v1/feed/gridfile',
			data: {'site': this.siteFiles.site, 'mode': 'getfile', 'file': fileName},
			link: 'cancel',
			timeout: 60000,
			responseType: 'arraybuffer',
			onSuccess: this.dataFileReceived.bind(this),
			onError: this.defaultErrorHandler.bind(this)
		}).get();
	}

	defaultErrorHandler(err) {
		console.log(err);
		this.inst.setLoadingMonitor(this.id, false);
		this.inst.setLoadingMonitor('nexradl2_sites', false);
	}

	dataFileReceived(resp) {
		// (ArrayBuffer) resp.body
		var cdmfile = new WECDM();
		cdmfile.readBuffer(resp.body);
		this.loadData(cdmfile);
		this.layer.settings.data = this.gateData;
		this.layer.render();
		this.inst.setLoadingMonitor(this.id, false);
	}
}




